import { useEffect, useRef, useId, useState, type ReactNode } from 'react';
import {
  X,
  Server,
  Globe2,
  Boxes,
  Package,
  Plus,
  ArrowUpRight,
  CalendarDays,
  Pause,
} from 'lucide-react';
import {
  attention,
  addMonths,
  daysBetween,
  cycles,
  money,
  shortDate,
  fullDate,
  categories,
  relevantDate,
  type Subscription,
} from '../lib/domain.ts';

export function Modal({
  title,
  eyebrow,
  children,
  onClose,
  wide = false,
  dirty = false,
  busy = false,
}: {
  title: string;
  eyebrow?: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  dirty?: boolean;
  busy?: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const [discardWarning, setDiscardWarning] = useState(false);
  const requestClose = () => {
    if (busy) return;
    if (dirty) setDiscardWarning(true);
    else onClose();
  };
  useEffect(() => {
    ref.current?.showModal();
    document.body.classList.add('modal-open');
    return () => {
      queueMicrotask(() => {
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('modal-open');
      });
    };
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={`modal ${wide ? 'wide' : ''}`}
      onCancel={(e) => {
        e.preventDefault();
        requestClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) {
          const r = ref.current!.getBoundingClientRect();
          if (
            e.clientX < r.left ||
            e.clientX > r.right ||
            e.clientY < r.top ||
            e.clientY > r.bottom
          )
            requestClose();
        }
      }}
    >
      <div className="modal-header">
        <div>
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          <h2 id={titleId}>{title}</h2>
        </div>
        <button
          type="button"
          className="icon-button"
          aria-label="关闭弹窗"
          disabled={busy}
          onClick={requestClose}
        >
          <X size={20} />
        </button>
      </div>
      {children}
      {discardWarning && (
        <div className="discard-warning" role="alert">
          <p>填写的内容还没有保存，要放弃这些修改吗？</p>
          <div>
            <button
              type="button"
              className="button secondary"
              onClick={() => setDiscardWarning(false)}
            >
              继续编辑
            </button>
            <button type="button" className="button danger" onClick={onClose}>
              放弃修改
            </button>
          </div>
        </div>
      )}
    </dialog>
  );
}

export function ServiceIcon({ sub, small = false }: { sub: Subscription; small?: boolean }) {
  const Icon = { server: Server, domain: Globe2, software: Boxes, other: Package }[sub.category];
  return (
    <span
      className={`service-icon ${small ? 'small' : ''}`}
      style={{ '--service-color': sub.color } as React.CSSProperties}
    >
      <Icon size={small ? 19 : 25} strokeWidth={1.8} />
    </span>
  );
}

export function StatusBadge({ sub, today }: { sub: Subscription; today: string }) {
  const state = attention(sub, today);
  return <span className={`status-badge ${state.kind}`}>{state.label}</span>;
}

export function TickProgress({ sub, today }: { sub: Subscription; today: string }) {
  const months = cycles[sub.cycle].months;
  const end = relevantDate(sub);
  const start =
    months && sub.nextPayment
      ? [addMonths(sub.nextPayment, -months, sub.paymentDay), sub.startDate].sort().at(-1)!
      : sub.startDate;
  const total = end ? Math.max(1, daysBetween(start, end)) : 1;
  const remaining = end ? Math.min(100, Math.max(0, (daysBetween(today, end) / total) * 100)) : 0;
  const state = attention(sub, today);
  return (
    <div className="tick-progress" aria-label={`当前周期剩余 ${Math.round(remaining)}%`}>
      <div className="tick-caption">
        <span>
          {sub.status === 'paused'
            ? '订阅已停用'
            : state.days < 0
              ? state.label
              : state.days === 0
                ? '今天需要留意'
                : Number.isFinite(state.days)
                  ? `距离${end === sub.expiryDate ? '到期' : '付款'}还有 ${state.days} 天`
                  : '长期有效'}
        </span>
        <strong>
          {sub.status === 'paused' ? <Pause size={13} /> : `${Math.round(remaining)}%`}
        </strong>
      </div>
      <div className={`ticks ${state.kind}`} aria-hidden="true">
        {Array.from({ length: 48 }, (_, i) => (
          <i
            key={i}
            className={`${i < Math.floor((48 * remaining) / 100) ? 'filled' : ''} ${i === Math.floor((48 * remaining) / 100) ? 'marker' : ''}`}
          />
        ))}
      </div>
    </div>
  );
}

export function SubscriptionCard({
  sub,
  today,
  onOpen,
  onPay,
}: {
  sub: Subscription;
  today: string;
  onOpen: (s: Subscription) => void;
  onPay: (s: Subscription) => void;
}) {
  return (
    <article
      className={`subscription-card ${sub.status === 'paused' ? 'is-paused' : ''}`}
      style={{ '--service-color': sub.color } as React.CSSProperties}
    >
      <div className="card-orbits" aria-hidden="true" />
      <button
        className="card-main"
        type="button"
        onClick={() => onOpen(sub)}
        aria-label={`查看 ${sub.name} 详情`}
      >
        <div className="service-top">
          <ServiceIcon sub={sub} />
          <div className="service-heading">
            <h3>{sub.name}</h3>
            <span>
              <i style={{ background: sub.color }} />
              {sub.provider || categories[sub.category].label}
              <span className="category-dot">·</span>
              {categories[sub.category].label}
            </span>
          </div>
          <div className="service-price">
            <strong>{money(sub.price, sub.currency, Number.isInteger(sub.price) ? 0 : 2)}</strong>
            <span>/ {cycles[sub.cycle].short}</span>
          </div>
        </div>
        <TickProgress sub={sub} today={today} />
        <div className="card-payment">
          <CalendarDays size={16} />
          <span>
            下次付款：<b>{fullDate(sub.nextPayment)}</b>
          </span>
          <StatusBadge sub={sub} today={today} />
        </div>
        <div className="card-dates">
          <span>
            开始 <b>{fullDate(sub.startDate)}</b>
          </span>
          <span>
            到期 <b>{fullDate(sub.expiryDate)}</b>
            {sub.expiryDate && sub.status === 'active' && (
              <em className="expiry-count">
                {daysBetween(today, sub.expiryDate) < 0
                  ? ` · 逾期 ${Math.abs(daysBetween(today, sub.expiryDate))} 天`
                  : ` · 余 ${daysBetween(today, sub.expiryDate)} 天`}
              </em>
            )}
          </span>
        </div>
      </button>
      <div className="card-footer">
        <span className={`renew-mode ${sub.autoRenew ? 'auto' : ''}`}>
          <span />
          {sub.autoRenew ? '自动续费' : '手动续费'}
          <span className="card-tag">{sub.tags[0] || sub.resource || '未设置标签'}</span>
        </span>
        {sub.status === 'active' && sub.nextPayment ? (
          <button type="button" onClick={() => onPay(sub)}>
            记录付款
            <ArrowUpRight size={13} />
          </button>
        ) : (
          <span className="muted">已停用</span>
        )}
      </div>
    </article>
  );
}

export function EmptyState({
  title = '这里还没有订阅',
  text = '点击添加订阅，记录日期和费用。',
  onAdd,
}: {
  title?: string;
  text?: string;
  onAdd?: () => void;
}) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <Package size={30} />
      </div>
      <h3>{title}</h3>
      <p>{text}</p>
      {onAdd && (
        <button className="button primary" onClick={onAdd}>
          <Plus size={17} />
          添加订阅
        </button>
      )}
    </div>
  );
}

export function UpcomingList({
  subscriptions,
  today,
  onOpen,
  limit = 5,
}: {
  subscriptions: Subscription[];
  today: string;
  onOpen: (s: Subscription) => void;
  limit?: number;
}) {
  const upcoming = subscriptions
    .filter((s) => s.status === 'active' && relevantDate(s))
    .sort((a, b) => relevantDate(a).localeCompare(relevantDate(b)))
    .slice(0, limit);
  return (
    <div className="upcoming-list">
      {upcoming.length ? (
        upcoming.map((sub) => (
          <button className="upcoming-item" key={sub.id} onClick={() => onOpen(sub)}>
            <ServiceIcon sub={sub} small />
            <span className="upcoming-name">
              <b>{sub.name}</b>
              <small>
                {shortDate(relevantDate(sub))} · {sub.autoRenew ? '自动续费' : '手动续费'}
              </small>
            </span>
            <span className="upcoming-amount">
              <b>{money(sub.price, sub.currency)}</b>
              <StatusBadge sub={sub} today={today} />
            </span>
          </button>
        ))
      ) : (
        <p className="panel-empty">暂时没有待付款的订阅</p>
      )}
    </div>
  );
}
